import { useCallback, useEffect, useRef, useState } from 'react';
import { STUDIO_STAGES, type StudioStage } from '@/lib/studio/studioState';

interface Input {
  isB: boolean;
  hasOutline: boolean;
  /** Whether hasOutline can be trusted yet: a fresh send knows at once; a reopened video once its outline has loaded. */
  known: boolean;
  stage: StudioStage;
}

/**
 * Direction B (beta only): a new video keeps Ivy centred until there is something to show. She moves to the rail, once,
 * when the storyboard's first page has drawn or you pick a view or a panel (`dock`), or when the build has a scene of the
 * plan to frame. Never on a timer, and never on a build signal before the plan has scenes. A return opens in the rail.
 */
export function useCentreStage({ isB, hasOutline, known, stage }: Input) {
  // Until it is known whether the video has a plan, Ivy waits in the rail: most such visits are a return to a video.
  const [docked, setDocked] = useState(() => !isB || hasOutline || !known);
  const [startedCentred, setStartedCentred] = useState(() => !docked);
  const decided = useRef(!isB || known);
  useEffect(() => {
    if (decided.current || !known) return;
    decided.current = true;
    if (!hasOutline) {
      setDocked(false);
      setStartedCentred(true);
    }
  }, [known, hasOutline]);
  const centred = isB && !docked;
  const dock = useCallback(() => setDocked(true), []);
  const building = STUDIO_STAGES.indexOf(stage) >= STUDIO_STAGES.indexOf('build');

  useEffect(() => {
    if (centred && building && hasOutline) setDocked(true);
  }, [centred, building, hasOutline]);

  const stageRef = useRef<HTMLDivElement>(null);
  const wasCentred = useRef(centred);
  useEffect(() => {
    if (wasCentred.current && !centred) stageIn(stageRef.current);
    wasCentred.current = centred;
  }, [centred]);

  return { centred, startedCentred, dock, stageRef };
}

/** The stage rises in from 24 px to the right behind Ivy's move; with reduced motion, a 150 ms crossfade. */
function stageIn(el: HTMLElement | null) {
  if (!el?.animate) return;
  const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  if (still) el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 150, easing: 'linear' });
  else el.animate([{ opacity: 0, transform: 'translateX(24px)' }, { opacity: 1, transform: 'none' }], { duration: 420, delay: 80, easing: 'cubic-bezier(0.2, 0, 0, 1)', fill: 'backwards' });
}
